import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  inject,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { Menu } from 'primeng/menu';

import { NgIconComponent } from '@ng-icons/core';

import { LoaderWrapperComponent } from '@components/loader-wrapper/loader-wrapper.component';

import { Notification } from '@models/notification.model';

import { NotificationAPI } from '@services/API/notify/notifications.service';
import { NotificationState } from '@services/state/notification.state';

import { humanizeISODate } from '@cxc/core/utils';

@Component({
  selector: 'notifications-list',
  standalone: true,
  imports: [Menu, LoaderWrapperComponent, ButtonModule, NgIconComponent],
  template: `
    <p-menu
      #notificationMenu
      [model]="notificationState.getNotifications()"
      [popup]="true"
      class="flex select-none justify-center [&_.p-menu-list_.p-menu-item-content]:!cursor-auto"
      styleClass="notification-menu h-[582px] flex flex-col"
      [style]="{ width: notificationMenuWidth + 'px' }"
      (onHide)="markAsViewed()">
      <ng-template #start>
        <div
          class="flex flex-col gap-1 border-b border-surface-300 p-4 text-surface-500">
          <h3 class="font-bold">Notifications</h3>
        </div>
      </ng-template>
      <ng-template #submenuheader let-item>
        <span class="font-bold text-primary">{{ item.label }}</span>
      </ng-template>

      @if (notificationState.getNotifications().length > 0) {
        <ng-template #item let-notification>
          <div
            class="flex items-center justify-between gap-1 border-b border-surface-300 !p-6">
            <div class="flex flex-col items-start gap-2">
              <small class="relative font-bold">
                @if (notification.status === 'UNREAD') {
                  <span
                    class="absolute -left-3 me-2 mt-[6px] inline-block h-2 w-2 rounded-full bg-cyan-500 p-1">
                  </span>
                }
                {{ notification.title }}
              </small>
              <small>{{ notification.description }}</small>
              <div class="flex flex-wrap gap-1">
                @for (action of notification.actions; track $index) {
                  <p-button
                    variant="text"
                    (click)="actionClick(action)"
                    size="small"
                    styleClass="!text-secondary-700"
                    class="">
                    {{ action.button_text }}

                    <ng-icon name="phosphorArrowRight" size="20px" />
                  </p-button>
                }
              </div>
            </div>
            <span class="self-baseline text-xs text-surface-500">{{
              humanizeISODate(notification.created_at)
            }}</span>
          </div>
        </ng-template>
      } @else {
        <ng-template #end>
          <loader-wrapper
            class="relative block h-full w-full"
            loaderKey="Notifications"
            [background]="false">
            <div class="flex h-full items-center justify-center">
              <div class="flex flex-col items-center justify-center gap-8">
                <img src="svgs/bell.svg" alt="No notifications" />
                <h3 class="font-light">
                  You don't have any notifications yet.
                </h3>
              </div>
            </div>
          </loader-wrapper>
        </ng-template>
      }
    </p-menu>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NotificationsList {
  humanizeISODate = humanizeISODate;
  notificationMenuWidth = 475;
  notificationState = inject(NotificationState);
  notificationAPI = inject(NotificationAPI);
  router = inject(Router);
  notificationMenu = viewChild.required<Menu>('notificationMenu');

  constructor() {
    this.notificationState.toggleMenuSubject$.subscribe(event => {
      this.notificationMenu().toggle(event);
    });
  }

  markAsViewed() {
    this.notificationState.getNotifications().forEach(notification => {
      if (notification.status === 'UNREAD') {
        this.notificationAPI.markAsViewed(notification.id, false).subscribe();
      }
    });
    this.notificationState.setNotificationCountToZero();
  }

  actionClick(action: Notification.Action | undefined): void {
    if (!action) return;
    switch (action.type) {
      case Notification.Action.Type.LINK_INTERNAL:
        // Bad formating, reject
        if (!action.payload.startsWith('/')) {
          console.error(
            `Wrong internal link provided at NotificationsComponent: '${action.payload}'`
          );
          return;
        }
        this.router.navigateByUrl(action.payload);
        break;

      case Notification.Action.Type.LINK_EXTERNAL:
        // Unsecure protocol, reject
        if (action.payload.startsWith('http://')) {
          console.error(
            `Unsafe URL provided at NotificationsComponent: '${action.payload}'`
          );
          return;
        }
        // Add secure protocol if not present
        if (!action.payload.startsWith('https://')) {
          action.payload = 'https://' + action.payload;
        }
        window.open(action.payload, '_blank')!.focus();
        break;

      default:
        break;
    }
  }
}
